import axios from "axios";
import ObjectId from "bson-objectid";
import dayjs from "dayjs";
import {
  BehaviorSubject,
  bufferTime,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  filter,
  first,
  firstValueFrom,
  from,
  groupBy,
  merge,
  mergeMap,
  NEVER,
  skip,
  Subject,
  Subscription,
  switchMap,
  throwError,
  timer,
  timeout,
  map,
} from "rxjs";

import packageJson from "../package.json";
import { DevicePlatform, type APIs } from "./apis/APIs";
import { createCrmEvent } from "./apis/createCrmEvent";
import { collectEvent } from "./apis/createEvent";
import { getItemRecommendations } from "./apis/getItemRecommendations";
import { initialize } from "./apis/initialize";
import { notificationsUpdate } from "./apis/notificationsUpdate";
import { signIn } from "./apis/signIn";
import { signOut } from "./apis/signOut";
import { updateProperties } from "./apis/updateProperties";
import { Bridge, BridgePlatform } from "./bridges/Bridge";
import { FlutterBridge } from "./bridges/FlutterBridge";
import { RNBridge } from "./bridges/RNBridge";
import { BLUX_ATTRIBUTES } from "./constants/BLUX_ATTRIBUTES";
import {
  AddClickEvent,
  AddPageVisitEvent,
  AddSectionViewEvent,
  VisitEvent,
} from "./events";
import { LocalStorage } from "./utils/LocalStorage";
import { Logger } from "./utils/Logger";
import { onlineAndVisible$ } from "./utils/observables";
import { filterNullable } from "./utils/operators";
import { SessionStorage } from "./utils/SessionStorage";

import type { Event as BluxEvent } from "./events/Event";
import type { EventRequest } from "./events/types";
import type { CategoryRec, ItemRec, ItemsRec, UserRec } from "./recs";
import type { LogLevel } from "./utils/Logger";
import type { AxiosInstance } from "axios";

export type SdkInfo = {
  type: DevicePlatform;
  version: string;
};

type BluxUser = {
  id: string;
  bluxAPIKey: string;
  applicationId: string;
  deviceId: string;
  userId?: string;
};

type BridgeOptions =
  | {
      /**
       * @deprecated `bridgePlatform`을 사용하세요.
       */
      useRNBridge?: boolean;
    }
  | { bridgePlatform?: BridgePlatform };

type InappMessageResponse = NonNullable<
  APIs.bluxUsersCollectEventsV2.ResponseData["inapp"]
>;

type ApiRequestQueueJob<T> = {
  action: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

type UserPropertiesQueueItem = {
  userProperties?: APIs.bluxUsersUpdateProperties.Body["user_properties"];
  customUserProperties?: APIs.bluxUsersUpdateProperties.Body["custom_user_properties"];
  resolve: () => void;
  reject: (error: unknown) => void;
};

export class BluxClient {
  private readonly api: AxiosInstance = axios.create();

  private readonly sdkInfo: SdkInfo = {
    type: DevicePlatform.browser,
    version: packageJson.version,
  };

  public readonly bluxUser$ = new BehaviorSubject<BluxUser | undefined>(
    undefined,
  );

  /**
   * 내부 async 작업을 1개씩 직렬로 처리하는 큐입니다.
   * `enqueueRequest()`로 넣으면, 호출자는 완료 시점까지 `await` 할 수 있습니다.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public readonly apiRequestQueue$ = new Subject<ApiRequestQueueJob<any>>();
  public readonly inappQueue$ = new BehaviorSubject<
    InappMessageResponse | undefined
  >(undefined);
  public readonly eventQueue$ = new Subject<EventRequest>();
  public readonly userPropertiesQueue$ = new Subject<UserPropertiesQueueItem>();

  private readonly INITIAL_NEXT_POLL_DELAY_MS = 10 * 1000;
  private readonly nextPollDelayMs$ = new BehaviorSubject<number | null>(
    this.INITIAL_NEXT_POLL_DELAY_MS,
  );
  private cachedNextPollDelayMs: number | null =
    this.INITIAL_NEXT_POLL_DELAY_MS;
  private lastPollTimestamp: number = Date.now();

  private readonly bluxApplicationId: string;
  private readonly bluxAPIKey: string;

  private readonly bridge: Bridge | null;

  // Impression 관련 변수
  private impressedElements: Element[] = [];
  private intersectionObservingElements: Element[] = [];
  private readonly intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        this.intersectionEntry$.next(entry);
      });
    },
  );

  private readonly intersectionEntry$ =
    new Subject<IntersectionObserverEntry>();

  // Hit 관련 변수
  private hitElements: Element[] = [];

  private resolveBridge(bridgeOptions: BridgeOptions): Bridge | null {
    if ("useRNBridge" in bridgeOptions && bridgeOptions.useRNBridge) {
      return new RNBridge();
    }

    if ("bridgePlatform" in bridgeOptions && bridgeOptions.bridgePlatform) {
      switch (bridgeOptions.bridgePlatform) {
        case "none":
          return null;
        case "react-native":
          return new RNBridge();
        case "flutter":
          return new FlutterBridge();
        default: {
          const exhaustiveCheck: never = bridgeOptions.bridgePlatform;
          Logger.error(`Unknown bridge platform: ${exhaustiveCheck}`, {
            tags: { from: "resolveBridge" },
          });
        }
      }
    }

    return null;
  }

  constructor(
    {
      bluxApplicationId,
      bluxAPIKey,
      ...bridgeOptions
    }: {
      bluxApplicationId: string;
      bluxAPIKey: string;
    } & BridgeOptions,
    completionCallback?: (args: {
      errorMessage?: string;
      success: boolean;
    }) => void,
  ) {
    this.bluxApplicationId = bluxApplicationId;
    this.bluxAPIKey = bluxAPIKey;

    const baseUrl = window?._BLUX_SDK_?.baseUrl ?? "https://api.blux.ai/prod";
    this.api.defaults.baseURL = baseUrl;

    this.bridge = this.resolveBridge(bridgeOptions);

    // api 요청 항상 하나씩 처리
    this.apiRequestQueue$
      .pipe(
        concatMap(async (job) => {
          try {
            const result = await job.action();
            job.resolve(result);
          } catch (error) {
            job.reject(error);
          }
        }),
      )
      .subscribe();

    /**
     * (1) 보내야할 이벤트를 100ms씩 뭉쳐서 보내고, 1개 이상 쌓여있을 때 api 요청
     * (2) collectEvent의 응답에서 온 nextPollDelayMs 값 만큼 이후에 빈 배열로 api 요청 (1)로 보내면 초기화됨
     */

    if (!this.bridge) {
      merge(
        this.eventQueue$.pipe(
          bufferTime(100),
          filter((requests) => requests.length > 0),
        ),
        // onlineAndVisible일 때만 polling, 남은 시간 계산하여 타이머 시작
        combineLatest([this.nextPollDelayMs$, onlineAndVisible$]).pipe(
          switchMap(([delay, isOnlineAndVisible]) => {
            if (!isOnlineAndVisible || delay == null) return NEVER;

            const targetTime = this.lastPollTimestamp + delay;
            const remainingDelay = Math.max(0, targetTime - Date.now());

            return timer(remainingDelay);
          }),
          map(() => [] as EventRequest[]),
        ),
      )
        .pipe(
          concatMap(async (requests) => {
            this.nextPollDelayMs$.next(null);
            try {
              const bluxUser = await this.getBluxUserWithTimeout();

              const events = requests.map((request) => ({
                ...request,
                blux_id: bluxUser.id,
                device_id: bluxUser.deviceId,
              }));

              const {
                data: { nextPollDelayMs, inapp },
              } = await this.enqueueRequest(() =>
                collectEvent(
                  this.api,
                  {
                    blux_user_id: bluxUser.id,
                    application_id: bluxUser.applicationId,
                  },
                  {
                    device_id: bluxUser.deviceId,
                    events,
                  },
                  this.generateApiHeader(bluxUser),
                ),
              );

              this.lastPollTimestamp = Date.now();
              this.cachedNextPollDelayMs = nextPollDelayMs;
              this.nextPollDelayMs$.next(Math.max(nextPollDelayMs, 3000));

              if (inapp) {
                this.inappQueue$.next(inapp);
              }
            } catch (error) {
              Logger.error(error, { tags: { from: "eventQueue" } });
              this.lastPollTimestamp = Date.now();
              if (this.cachedNextPollDelayMs !== null) {
                const nextPollDelay =
                  this.cachedNextPollDelayMs > 1000 * 60 * 60 * 24
                    ? this.cachedNextPollDelayMs
                    : this.cachedNextPollDelayMs * 2;
                this.cachedNextPollDelayMs = nextPollDelay;
                this.nextPollDelayMs$.next(nextPollDelay);
              }
            }
          }),
        )
        .subscribe();
    }

    // api 호출 수 절감을 위해 100ms씩 뭉쳐서 보내고, custom_user_properties와 user_properties를 합쳐서 보냄
    this.userPropertiesQueue$
      .pipe(
        bufferTime(100),
        filter((items) => items.length > 0),
        concatMap(async (items) => {
          try {
            const bluxUser = await this.getBluxUserWithTimeout();

            const mergedUserProperties = items.reduce(
              (acc, item) => ({ ...acc, ...(item.userProperties ?? {}) }),
              {} as NonNullable<
                APIs.bluxUsersUpdateProperties.Body["user_properties"]
              >,
            );

            const mergedCustomUserProperties = items.reduce(
              (acc, item) => ({ ...acc, ...(item.customUserProperties ?? {}) }),
              {} as NonNullable<
                APIs.bluxUsersUpdateProperties.Body["custom_user_properties"]
              >,
            );

            const payload: APIs.bluxUsersUpdateProperties.Body = {};
            if (Object.keys(mergedUserProperties).length > 0) {
              payload.user_properties = mergedUserProperties;
            }
            if (Object.keys(mergedCustomUserProperties).length > 0) {
              payload.custom_user_properties = mergedCustomUserProperties;
            }

            if (Object.keys(payload).length === 0) {
              items.forEach((item) => item.resolve());
              return;
            }

            await this.enqueueRequest(() =>
              updateProperties(
                this.api,
                {
                  application_id: bluxUser.applicationId,
                  blux_user_id: bluxUser.id,
                },
                payload,
                this.generateApiHeader(bluxUser),
              ),
            );

            items.forEach((item) => item.resolve());
          } catch (error) {
            items.forEach((item) => item.reject(error));
            Logger.error(error, { tags: { from: "userPropertiesQueue" } });
          }
        }),
      )
      .subscribe();

    this.bluxUser$.subscribe((bluxUser) => {
      if (bluxUser) SessionStorage.setBluxUserId(bluxUser.id);
    });

    if (!this.bridge) {
      this.inappQueue$
        .pipe(filterNullable())
        .pipe(
          concatMap((inapp) =>
            onlineAndVisible$.pipe(
              first(Boolean),
              concatMap(() => from(this.displayInapp(inapp))),
            ),
          ),
        )
        .subscribe();
    }

    this.init()
      .then(() => {
        completionCallback?.({ success: true });
      })
      .catch((error) => {
        completionCallback?.({ success: false, errorMessage: error.message });
      });
  }

  private async enqueueRequest<T>(action: () => Promise<T>) {
    return new Promise<T>((resolve, reject) => {
      const item: ApiRequestQueueJob<T> = { action, resolve, reject };
      this.apiRequestQueue$.next(item);
    });
  }

  private generateApiHeader({ bluxAPIKey }: { bluxAPIKey: string }) {
    return {
      Authorization: bluxAPIKey,
    };
  }

  private async displayInapp(inapp: InappMessageResponse) {
    return new Promise((resolve) => {
      const bluxUser = this.bluxUser$.getValue();

      const hideTimestamp = LocalStorage.getInappHideTimestamp(inapp.inappId);
      const isShouldSkip =
        hideTimestamp !== null && dayjs().isBefore(dayjs(hideTimestamp));

      if (isShouldSkip) return resolve(true);

      const iframeElement = document.createElement("iframe");

      const params = new URLSearchParams({
        inapp_id: inapp.inappId,
        application_id: this.bluxApplicationId,
        stage:
          this.api.defaults.baseURL === "https://api.blux.ai/prod"
            ? "prod"
            : "stg",
        sdk_version: this.sdkInfo.version,
      });

      iframeElement.src = `${inapp.baseUrl}?${params.toString()}`;
      iframeElement.style.position = "fixed";
      iframeElement.style.width = `${window.innerWidth}px`;
      iframeElement.style.height = `${window.innerHeight}px`;
      iframeElement.style.top = "0";
      iframeElement.style.left = "0";

      iframeElement.style.zIndex = "9999";
      iframeElement.id = `blux-inapp-${inapp.inappId}`;

      document.body.appendChild(iframeElement);

      let urlChangeSubscription: Subscription | null = null;

      const cleanup = () => {
        iframeElement.remove();
        window.removeEventListener("message", handleMessage);
        window.visualViewport?.removeEventListener(
          "resize",
          handleViewportChange,
        );
        window.visualViewport?.removeEventListener(
          "scroll",
          handleViewportChange,
        );
        urlChangeSubscription?.unsubscribe();
        resolve(inapp.inappId);
      };
      const handleViewportChange = () => {
        const width = window.visualViewport?.width ?? window.innerWidth;
        const height = window.visualViewport?.height ?? window.innerHeight;
        const left = window.visualViewport?.offsetLeft ?? 0;
        const top = window.visualViewport?.offsetTop ?? 0;

        iframeElement.style.width = `${width}px`;
        iframeElement.style.height = `${height}px`;
        iframeElement.style.left = `${left}px`;
        iframeElement.style.top = `${top}px`;
      };

      const handleMessage = (
        event: MessageEvent<
          | {
              action: "hide";
              data: {
                days_to_hide: number;
              };
            }
          | {
              action: "link";
              data:
                | {
                    is_blux_landing: false;
                    url: string;
                    open_in_new_tab: boolean;
                    /**
                     * Safari 팝업 차단 우회를 위해 iframe에서 직접 링크를 연 경우 true
                     * 이 플래그가 true면 window.open을 스킵하고 이벤트만 찍음
                     */
                    opened_by_iframe?: boolean;
                  }
                | {
                    is_blux_landing: true;
                    landing_id: string;
                  };
            }
          | {
              action: "resize";
              data: {
                height: number;
                location: "top" | "bottom";
              };
            }
        >,
      ) => {
        if (event.source !== iframeElement.contentWindow) return;

        const message = event.data;

        if (message.action === "resize") {
          const { height, location } = message.data;
          // 배너용 iframe 스타일 조정
          iframeElement.style.width = "90vw";
          iframeElement.style.maxWidth = "448px";
          iframeElement.style.height = `${height}px`;
          iframeElement.style.left = "50%";
          iframeElement.style.transform = "translateX(-50%)";
          if (location === "top") {
            iframeElement.style.top = "0";
            iframeElement.style.bottom = "auto";
          } else {
            iframeElement.style.top = "auto";
            iframeElement.style.bottom = "0";
          }

          window.visualViewport?.removeEventListener(
            "resize",
            handleViewportChange,
          );
          window.visualViewport?.removeEventListener(
            "scroll",
            handleViewportChange,
          );
          return; // resize는 cleanup하지 않음
        }

        if (message.action === "hide") {
          const { days_to_hide } = message.data;
          if (days_to_hide !== 0) {
            const expiresAt = dayjs().add(days_to_hide, "days").valueOf();
            LocalStorage.setInappHideTimestamp(inapp.inappId, expiresAt);
          }
        } else if (message.action === "link") {
          // createCrmEvent 호출
          const crmEventPromise = bluxUser
            ? this.enqueueRequest(() =>
                createCrmEvent(
                  this.api,
                  {
                    application_id: this.bluxApplicationId,
                  },
                  {
                    notification_id: inapp.notificationId,
                    crm_event_type: "inapp_opened",
                    captured_at: new Date().toISOString(),
                  },
                  this.generateApiHeader(bluxUser),
                ),
              )
            : Promise.resolve();

          if (message.data.is_blux_landing) {
            // TODO: @all 스키마상에는 개인화 랜딩이 존재하나, 콘솔에는 필드가 없음 -> 추후 이에 따른 핸들링이 필요함
          } else {
            const { url, open_in_new_tab, opened_by_iframe } = message.data;
            const needsLocationChange = opened_by_iframe && !open_in_new_tab;

            // 하고하우스 특별 처리
            if (this.bluxApplicationId === "6878af275ead5a75abbdacd7") {
              crmEventPromise.finally(() => {
                window.top?.postMessage(
                  {
                    type: "blux_inapp_message",
                    url,
                  },
                  window.location.origin,
                );
              });
            } else if (needsLocationChange) {
              // opened_by_iframe이고 현재 탭 이동인 경우: createCrmEvent 완료 후 이동
              crmEventPromise.finally(() => {
                window.location.href = url;
              });
            } else if (!opened_by_iframe) {
              // 기존 방식 (iframe에서 열지 않은 경우)
              if (open_in_new_tab) {
                window.open(url, "_blank");
              } else {
                window.location.href = url;
              }
            }
            // opened_by_iframe && open_in_new_tab인 경우: iframe에서 이미 새 탭 열었으므로 스킵
          }
        }

        cleanup();
      };

      // URL 변경 시 인앱메시지 닫기 (SPA 뒤로가기/앞으로가기 대응)
      urlChangeSubscription = this.urlAndRef$
        .pipe(filterNullable(), skip(1), first())
        .subscribe(() => {
          cleanup();
        });

      window.visualViewport?.addEventListener("resize", handleViewportChange);
      window.visualViewport?.addEventListener("scroll", handleViewportChange);
      window.addEventListener("message", handleMessage);

      if (bluxUser) {
        this.enqueueRequest(() =>
          notificationsUpdate(
            this.api,
            {
              application_id: this.bluxApplicationId,
              notification_id: inapp.notificationId,
            },
            {
              status: "received",
            },
            this.generateApiHeader(bluxUser),
          ),
        );
      }
    });
  }

  private async init() {
    this.handleUrlAndRef();
    // handle page visit event
    this.urlAndRef$.pipe(filterNullable()).subscribe(() => {
      this.sendEvent(new AddPageVisitEvent({}));
    });

    if (this.bridge) {
      this.bridge.initialize({
        bluxApplicationId: this.bluxApplicationId,
        bluxAPIKey: this.bluxAPIKey,
      });
      return;
    }

    const blux_user_id = new URLSearchParams(window.location.search).get(
      "blux_user_id",
    );
    const sessionId =
      SessionStorage.getSessionId() ?? new ObjectId().toHexString();
    SessionStorage.setSessionId(sessionId);

    const isBluxUserIdExistInSessionStorage = SessionStorage.getBluxUserId();
    if (!isBluxUserIdExistInSessionStorage) this.sendEvent(new VisitEvent({}));

    Logger.debug(`Init with application id: ${this.bluxApplicationId}`);

    const deviceId = LocalStorage.getDeviceId();

    if (!deviceId) {
      Logger.debug("No saved Device ID, register a new one.");
    }

    this.registerHitTracker();

    // 페이지 이동 시 추천 지표 수집 관련 변수 초기화
    this.urlAndRef$.pipe(filterNullable()).subscribe(() => {
      this.impressedElements = [];
    });

    this.registerImpressionTracker();
    new MutationObserver(() => {
      this.registerImpressionTracker();
    }).observe(document, { subtree: true, childList: true });

    // 500ms 연속으로 엘리먼트 들어있으면 추천 지표 수집
    const MIN_IMPRESSION_TIME = 500;
    this.intersectionEntry$
      .pipe(
        groupBy((entry) => entry.target),
        mergeMap((group$) =>
          group$.pipe(
            debounceTime(MIN_IMPRESSION_TIME),
            filter((entry) => entry.isIntersecting),
          ),
        ),
      )
      .subscribe((entry) => {
        const trackingId = entry.target.getAttribute(
          BLUX_ATTRIBUTES.tracking_id,
        );
        if (!trackingId) {
          Logger.warning(
            `Tracking ID is not found in the parent elements. Please check the tracking ID(${BLUX_ATTRIBUTES.tracking_id}) is set correctly.`,
          );
          return;
        }
        if (this.impressedElements.includes(entry.target)) return;

        this.sendEvent(
          new AddSectionViewEvent({
            section: trackingId,
            tracking: {
              id: trackingId,
              type: "impression",
            },
          }),
        );
        this.impressedElements.push(entry.target);
      });

    const { data } = await initialize(
      this.api,
      { application_id: this.bluxApplicationId },
      {
        session_id: sessionId ?? undefined,
        device_id: deviceId ?? undefined,
        country_code: navigator.language.split("-")[1],
        device_model: navigator.userAgent,
        language_code: navigator.language.split("-")[0],
        platform: DevicePlatform.browser,
        os_version: navigator.userAgent,
        sdk_version: this.sdkInfo.version,
        sdk_type: this.sdkInfo.type,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        blux_user_id: blux_user_id ?? undefined,
      },
      this.generateApiHeader({
        bluxAPIKey: this.bluxAPIKey,
      }),
    );

    if (data.device_id) LocalStorage.setDeviceId(data.device_id);

    this.bluxUser$.next({
      id: data.blux_user_id,
      bluxAPIKey: this.bluxAPIKey,
      applicationId: this.bluxApplicationId,
      deviceId: data.device_id,
    });
  }

  public setLogLevel(logLevel: LogLevel) {
    Logger.setLevel(logLevel);
  }

  private async getBluxUserWithTimeout() {
    const bluxUser = await firstValueFrom(
      this.bluxUser$.pipe(
        filterNullable(),
        timeout(5000),
        catchError((error) => {
          return throwError(() =>
            error.name === "TimeoutError"
              ? new Error(
                  "Application ID is not initialized. Please initialize BluxClient.",
                )
              : error,
          );
        }),
      ),
    );

    return bluxUser;
  }

  public async signIn({ userId }: { userId: string }) {
    try {
      if (this.bridge) {
        this.bridge.signIn({ userId });
        return;
      }

      const bluxUser = await this.getBluxUserWithTimeout();

      const {
        data: { blux_user_id: bluxIdInResponse },
      } = await this.enqueueRequest(() =>
        signIn(
          this.api,
          { application_id: bluxUser.applicationId, blux_user_id: bluxUser.id },
          { user_id: userId, device_id: bluxUser.deviceId },
          this.generateApiHeader(bluxUser),
        ),
      );

      if (bluxIdInResponse !== bluxUser.id) {
        Logger.debug("Blux id is changed in signIn.");
        this.bluxUser$.next({
          ...bluxUser,
          id: bluxIdInResponse,
          userId,
        });
      }
    } catch (error) {
      Logger.error(error, { tags: { from: "signIn" } });
    }
  }

  public async signOut() {
    try {
      if (this.bridge) {
        this.bridge.signOut();
        return;
      }

      const bluxUser = await this.getBluxUserWithTimeout();

      const {
        data: { blux_user_id: bluxIdInResponse },
      } = await this.enqueueRequest(() =>
        signOut(
          this.api,
          { application_id: bluxUser.applicationId, blux_user_id: bluxUser.id },
          { device_id: bluxUser.deviceId },
          this.generateApiHeader(bluxUser),
        ),
      );

      Logger.debug("Blux id is changed in signOut.");
      this.bluxUser$.next({
        ...bluxUser,
        id: bluxIdInResponse,
        userId: undefined,
      });
    } catch (error) {
      Logger.error(error, { tags: { from: "signOut" } });
    }
  }

  public async setUserProperties({
    userProperties,
  }: {
    userProperties: NonNullable<
      APIs.bluxUsersUpdateProperties.Body["user_properties"]
    >;
  }) {
    try {
      if (this.bridge) {
        this.bridge.setUserProperties(userProperties);
        return;
      }

      await new Promise<void>((resolve, reject) => {
        this.userPropertiesQueue$.next({
          userProperties,
          resolve,
          reject,
        });
      });
    } catch (error) {
      Logger.error(error, { tags: { from: "setUserProperties" } });
    }
  }

  public async setCustomUserProperties({
    customUserProperties,
  }: {
    customUserProperties: NonNullable<
      APIs.bluxUsersUpdateProperties.Body["custom_user_properties"]
    >;
  }) {
    try {
      if (this.bridge) {
        this.bridge.setCustomUserProperties(customUserProperties);
        return;
      }

      await new Promise<void>((resolve, reject) => {
        this.userPropertiesQueue$.next({
          customUserProperties,
          resolve,
          reject,
        });
      });
    } catch (error) {
      Logger.error(error, { tags: { from: "setCustomUserProperties" } });
    }
  }

  public sendEvent(event: BluxEvent | BluxEvent[]) {
    try {
      const requests = (Array.isArray(event) ? event : [event]).map((e) => ({
        ...e.request,
        internal_event_properties: {
          url: this.urlAndRef$.value?.url,
          ref: this.urlAndRef$.value?.ref,
        },
      }));

      if (this.bridge) {
        this.bridge.sendEvent({ requests });
        return;
      }

      requests.forEach((request) => {
        this.eventQueue$.next(request);
      });
    } catch (error) {
      Logger.error(error, { tags: { from: "sendEvent" } });
    }
  }

  private urlAndRef$ = new BehaviorSubject<
    { url: string; ref: string | undefined } | undefined
  >(undefined);

  private handleUrlAndRef() {
    // set url and ref to local storage
    this.urlAndRef$.pipe(filterNullable()).subscribe(({ url, ref }) => {
      if (ref) {
        LocalStorage.setRef(ref);
      }
      LocalStorage.setUrl(url);
    });
    // init url and ref from local storage
    this.urlAndRef$.next({
      url: window.location.href,
      ref: LocalStorage.getUrl() ?? undefined,
    });

    // observe url change
    const handleUrlChange = () => {
      const prevUrl = this.urlAndRef$.value?.url;
      const currentUrl = window.location.href;
      if (prevUrl !== currentUrl) {
        this.urlAndRef$.next({
          url: currentUrl,
          ref: prevUrl ?? undefined,
        });
      }
    };

    // SPA: popstate (뒤로가기/앞으로가기)
    window.addEventListener("popstate", handleUrlChange);
    // 현재 브라우저에는 URL상태를 추적하기 위한 리스너가 존재하지 않습니다.
    // 그래서 pushState와 replaceState를 직접 수정하여 추적합니다.
    // @reference https://stackoverflow.com/a/64927639
    // @reference https://github.com/amplitude/Amplitude-TypeScript/blob/main/packages/plugin-page-view-tracking-browser/src/page-view-tracking.ts
    // SPA: pushState/replaceState proxy
    history.pushState = new Proxy(history.pushState, {
      apply: (target, thisArg, [state, unused, url]) => {
        target.apply(thisArg, [state, unused, url]);
        handleUrlChange();
      },
    });
    history.replaceState = new Proxy(history.replaceState, {
      apply: (target, thisArg, [state, unused, url]) => {
        target.apply(thisArg, [state, unused, url]);
        handleUrlChange();
      },
    });
  }

  private registerImpressionTracker() {
    const targetElements = Array.from(
      document.querySelectorAll(`[${BLUX_ATTRIBUTES.tracking_id}]`),
    );

    const removed = this.intersectionObservingElements.filter(
      (e) => !targetElements.includes(e),
    );

    removed.forEach((e) => {
      this.intersectionObserver.unobserve(e);
    });

    const added = targetElements.filter(
      (element) => !this.intersectionObservingElements.includes(element),
    );

    added.forEach((element) => {
      this.intersectionObserver.observe(element);
    });

    this.intersectionObservingElements = targetElements;
  }

  private registerHitTracker() {
    const handleDelegatedClick = (event: Event) => {
      if (!(event.target instanceof Element)) return;

      const eventTarget = event.target;

      const targetItemElement = eventTarget.closest(
        `[${BLUX_ATTRIBUTES.item_id}]`,
      );
      if (!targetItemElement) return;

      const itemId = targetItemElement.getAttribute(BLUX_ATTRIBUTES.item_id);
      if (!itemId) return;

      const targetSectionElement = eventTarget.closest(
        `[${BLUX_ATTRIBUTES.tracking_id}]`,
      );
      const trackingId = targetSectionElement?.getAttribute(
        BLUX_ATTRIBUTES.tracking_id,
      );
      if (!trackingId) {
        Logger.warning(
          `Tracking ID is not found in the parent elements. Please check the tracking ID(${BLUX_ATTRIBUTES.tracking_id}) is set correctly.`,
        );
        return;
      }

      if (!this.hitElements.includes(targetItemElement)) {
        this.sendEvent(
          new AddClickEvent({
            itemId,
            tracking: {
              id: trackingId,
              type: "hit",
            },
          }),
        );
        this.hitElements.push(targetItemElement);
      }
    };
    document.addEventListener("click", handleDelegatedClick);
    this.urlAndRef$.pipe(filterNullable()).subscribe(() => {
      this.hitElements = [];
    });
  }

  public async getUserRec(
    rec: UserRec,
  ): Promise<APIs.bluxUsersGetItemRecommendationsV2.ResponseData> {
    try {
      const bluxUser = await this.getBluxUserWithTimeout();
      const recRequest = rec.request;

      const recommendationResponse = await getItemRecommendations(
        this.api,
        {
          application_id: bluxUser.applicationId,
          blux_user_id: bluxUser.id,
        },
        {
          call_type: recRequest.call_type,
          limit: recRequest.limit,
          offset: recRequest.offset,
        },
        this.generateApiHeader(bluxUser),
      );

      return recommendationResponse.data;
    } catch (error) {
      Logger.error(error, { tags: { from: "getItemRecommendations" } });
      return {
        item_ids: [],
        metadata: {},
        recommendation_id: null,
      };
    }
  }

  public async getItemRec(
    rec: ItemRec,
  ): Promise<APIs.bluxUsersGetItemRecommendationsV2.ResponseData> {
    try {
      const bluxUser = await this.getBluxUserWithTimeout();
      const recRequest = rec.request;

      const recommendationResponse = await getItemRecommendations(
        this.api,
        {
          application_id: bluxUser.applicationId,
          blux_user_id: bluxUser.id,
        },
        {
          call_type: recRequest.call_type,
          limit: recRequest.limit,
          offset: recRequest.offset,
          item_id: recRequest.item_id,
        },
        this.generateApiHeader(bluxUser),
      );

      return recommendationResponse.data;
    } catch (error) {
      Logger.error(error, { tags: { from: "getItemRecommendations" } });
      return {
        item_ids: [],
        metadata: {},
        recommendation_id: null,
      };
    }
  }

  public async getItemsRec(
    rec: ItemsRec,
  ): Promise<APIs.bluxUsersGetItemRecommendationsV2.ResponseData> {
    try {
      const bluxUser = await this.getBluxUserWithTimeout();
      const recRequest = rec.request;

      const recommendationResponse = await getItemRecommendations(
        this.api,
        {
          application_id: bluxUser.applicationId,
          blux_user_id: bluxUser.id,
        },
        {
          call_type: recRequest.call_type,
          limit: recRequest.limit,
          offset: recRequest.offset,
          item_ids: recRequest.item_ids,
        },
        this.generateApiHeader(bluxUser),
      );

      return recommendationResponse.data;
    } catch (error) {
      Logger.error(error, { tags: { from: "getItemRecommendations" } });
      return {
        item_ids: [],
        metadata: {},
        recommendation_id: null,
      };
    }
  }

  public async getCategoryRec(
    rec: CategoryRec,
  ): Promise<APIs.bluxUsersGetItemRecommendationsV2.ResponseData> {
    try {
      const bluxUser = await this.getBluxUserWithTimeout();
      const recRequest = rec.request;

      const recommendationResponse = await getItemRecommendations(
        this.api,
        {
          application_id: bluxUser.applicationId,
          blux_user_id: bluxUser.id,
        },
        {
          call_type: recRequest.call_type,
          limit: recRequest.limit,
          offset: recRequest.offset,
          category_id: recRequest.category_id,
        },
        this.generateApiHeader(bluxUser),
      );

      return recommendationResponse.data;
    } catch (error) {
      Logger.error(error, { tags: { from: "getItemRecommendations" } });
      return {
        item_ids: [],
        metadata: {},
        recommendation_id: null,
      };
    }
  }
}
